import { createAsyncThunk } from "@reduxjs/toolkit";
import { notification } from "antd";
import { slices } from "@/constants/sliceNames";
import axiosIns from "@/axios/defaultAxios";
import { DataSource } from "@/entities/DataSource";
import { serializeQueryParams } from "@/utils/utils";
import { DataSourceMonitorData } from "@/entities/DataSource/DataSourceMonitorData";
import blobAxios, { downloadBlob } from "@/axios/blobDownloadAxios";
import { TaskTypes } from "@/constants/task-types";
import { Adjacent, Assignment, Prediction, ReviewStats } from "@/entities/DataSource/DataSource";
import Axios from "axios";

import { errorMessage, successMessage } from "../../actions/messages.actions";

const ENDPOINT = "/monitors";
const MONITOR_DATA_ENDPOINT = "/hoya/v3/monitor/:id/data/side/:side";
const EXPORT_MONITOR_DATA_ENDPOINT = "/hoya/v3/monitor/:id/export";
const DEFAULT_IMAGE_DETAILS_ENDPOINT = "/monitors/:id/predictions/single";
const HOYA_IMAGE_DETAILS_ENDPOINT = "/hoya/v3/monitor/image-details/:imageId";
const MONITOR_PLATE_DATA_ENDPOINT = "/hoya/v3/monitor";
const CLASSIFICATION_PLATE_DATA_ENDPOINT = "/hoya/v3/classification/monitor";

const getBaseEndpoint = (type) => {
  return type === TaskTypes.CLASSIFICATION ? CLASSIFICATION_PLATE_DATA_ENDPOINT : MONITOR_PLATE_DATA_ENDPOINT;
};

/******* Get list of data sources ********/

export const requestDataSourcesList = createAsyncThunk(
  `${slices.datasource}/requestDataSourcesList`,
  async (params = { sort: "-updated_at" }, { rejectWithValue, dispatch }) => {
    const url = `${ENDPOINT}?${serializeQueryParams(params)}`;
    try {
      const response = await axiosIns.get(url);
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestDataSourcesListStatus = {
  [requestDataSourcesList.fulfilled]: (state, action) => {
    const { current_page, page_limit, records_count, total_pages, data } = action.payload;
    state.datasourceList = data.map(DataSource.fromApi);
    state.currentPage = current_page;
    state.pageLimit = page_limit;
    state.recordsCount = records_count;
    state.totalPages = total_pages;
    state.loading = false;
  },
  [requestDataSourcesList.pending]: (state, action) => {
    state.loading = true;
  },
  [requestDataSourcesList.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
  },
};

/******* Get single data source details ********/

export const requestSingleDataSourceDetails = createAsyncThunk(
  `${slices.datasource}/requestSingleDataSourceDetails`,
  async (datasourceId, { rejectWithValue, dispatch }) => {
    const url = `${ENDPOINT}/${datasourceId}`;
    try {
      const response = await axiosIns.get(url);
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestSingleDataSourceDetailsStatus = {
  [requestSingleDataSourceDetails.fulfilled]: (state, action) => {
    state.datasource = DataSource.fromApi(action.payload);
    state.loading = false;
  },
  [requestSingleDataSourceDetails.pending]: (state, action) => {
    state.loading = true;
  },
  [requestSingleDataSourceDetails.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
  },
};

/******* Connect data source ********/

export const requestConnectDataSource = createAsyncThunk(
  `${slices.datasource}/requestConnectDataSource`,
  async (datasourceId, { rejectWithValue, dispatch }) => {
    const url = `${ENDPOINT}/${datasourceId}/connect`;
    try {
      const response = await axiosIns.post(url);
      if (response.status === 200) {
        dispatch(successMessage("Success", "Datasource connected Successfully"));
        return response.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestConnectDataSourceStatus = {
  [requestConnectDataSource.fulfilled]: (state, action) => {
    state.connection.loading = false;
    state.connection.error = "";
    state.connection.message = action.payload.message;
  },
  [requestConnectDataSource.pending]: (state, action) => {
    state.connection.loading = true;
    state.connection.error = "";
    state.connection.message = "";
  },
  [requestConnectDataSource.rejected]: (state, action) => {
    state.connection.loading = false;
    state.connection.error = action.payload;
    state.connection.message = "";
  },
};

/******* Disconnect data source ********/

export const requestDisconnectDataSource = createAsyncThunk(
  `${slices.datasource}/requestDisconnectDataSource`,
  async (datasourceId, { rejectWithValue, dispatch }) => {
    const url = `${ENDPOINT}/${datasourceId}/disconnect`;
    try {
      const response = await axiosIns.post(url);
      if (response.status === 200) {
        dispatch(successMessage("Success", "Datasource disconnected Successfully"));
        return response.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestDisconnectDataSourceStatus = {
  [requestDisconnectDataSource.fulfilled]: (state, action) => {
    state.connection.loading = false;
    state.connection.error = "";
    state.connection.message = action.payload.message;
    // state.datasource.connected = false;
    state.loading = false;
  },
  [requestDisconnectDataSource.pending]: (state, action) => {
    state.loading = true;
    state.connection.error = "";
    state.connection.message = "";
  },
  [requestDisconnectDataSource.rejected]: (state, action) => {
    state.connection.loading = false;
    state.connection.error = action.payload;
    state.connection.message = "";
  },
};

// /******* Create Data source ********/

export const requestCreateDataSourceStreaming = createAsyncThunk(
  `${slices.dataSource}/requestCreateDataSourceStreaming`,
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.post(`${ENDPOINT}`, data);
      if (response.status === 200) {
        dispatch(successMessage(response.data.message));
        return response.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const requestCreateDataSourceStreamingStatus = {
  [requestCreateDataSourceStreaming.fulfilled]: (state, action) => {
    state.loading = false;
    state.error = "";
  },
  [requestCreateDataSourceStreaming.pending]: (state, action) => {
    state.loading = true;
    state.error = "";
  },
  [requestCreateDataSourceStreaming.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
  },
};

/******* Delete datasource ********/

export const requestDeleteDataSource = createAsyncThunk(
  `${slices.datasource}/requestDeleteDataSource`,
  async (datasourceId, { rejectWithValue, dispatch }) => {
    const url = `${ENDPOINT}/${datasourceId}`;
    try {
      const response = await axiosIns.delete(url);
      if (response.status === 200) {
        dispatch(successMessage(response.data.message));
        return response.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const requestDeleteDataSourceStatus = {
  [requestDeleteDataSource.fulfilled]: (state, action) => {
    state.loading = false;
    state.error = "";
    state.message = action.payload.message;
  },
  [requestDeleteDataSource.pending]: (state, action) => {
    state.loading = true;
    state.error = "";
    state.message = "";
  },
  [requestDeleteDataSource.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
    state.message = "";
  },
};

/******* Get single data source details ********/

export const requestDataSourceMonitorData = createAsyncThunk(
  `${slices.datasource}/requestDataSourceMonitorData`,
  async ({ monitorId, params }, { rejectWithValue, dispatch, getState }) => {
    if (params.withoutLoading) {
      // preserve page by adding to page limit to refresh all the data
      const { itemsCurrentPage, itemsPageLimit } = getState()?.[slices.datasource];
      params["page_limit"] = itemsPageLimit * itemsCurrentPage;
    }
    const url = `${MONITOR_DATA_ENDPOINT.replace(":id", monitorId).replace(
      ":side",
      params["side"]
    )}?${serializeQueryParams(params)}`;
    try {
      const response = await axiosIns.get(url);
      const { data, ...rest } = response.data.data;
      const monitorData = DataSourceMonitorData.fromApi(data);
      return { monitorData, ...rest };
    } catch (error) {
      if (error.response?.status !== 404) {
        dispatch(errorMessage(error.response?.data?.error ?? error.message));
      }
      return rejectWithValue(error.response?.data?.error ?? error.message);
    }
  }
);

export const requestDataSourceMonitorDataStatus = {
  [requestDataSourceMonitorData.fulfilled]: (state, action) => {
    const { monitorData, current_page, records_count, page_limit } = action.payload;
    state.dataLoading = false;
    state.itemData =
      current_page === 1
        ? monitorData
        : new DataSourceMonitorData(
            monitorData.getHeader(),
            [...state.itemData.rows, ...monitorData.rows],
            [...state.itemData.getImagesMetadata(), ...monitorData.getImagesMetadata()]
          );
    state.totalItemData = records_count;
    state.itemsCurrentPage = current_page;
    state.itemsPageLimit = page_limit;
  },
  [requestDataSourceMonitorData.pending]: (state, action) => {
    if (!action.meta?.arg?.params?.withoutLoading) {
      state.dataLoading = true;
    }
  },
  [requestDataSourceMonitorData.rejected]: (state, action) => {
    state.dataLoading = false;
    state.itemData = new DataSourceMonitorData();
    state.totalItemData = 1;
  },
};

/******* Export data source monitor by monitor ID ********/

export const requestDataSourceMonitorDataForExport = (monitorId, params) => {
  const url = `${EXPORT_MONITOR_DATA_ENDPOINT.replace(":id", monitorId)}?${serializeQueryParams(params)}`;
  return async (dispatch) => {
    try {
      const res = await axiosIns.get(url);
      return res.data.data;
    } catch (err) {
      dispatch(errorMessage(err.response?.data?.error ?? err.message));
    }
  };
};

/******* Get hoya datasource image details ********/

export const requestHoyaDataSourceImageDetails = createAsyncThunk(
  `${slices.datasource}/requestHoyaDataSourceImageDetails`,
  async ({ imageId, params }, { rejectWithValue, dispatch }) => {
    const url = HOYA_IMAGE_DETAILS_ENDPOINT.replace(":imageId", imageId);
    try {
      const response = await axiosIns.get(url + "?" + serializeQueryParams(params));
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const requestHoyaDataSourceImageDetailsStatus = {
  [requestHoyaDataSourceImageDetails.fulfilled]: (state, action) => {
    state.imageDetails = action.payload;
    state.loading = false;
    state.error = "";
  },
  [requestHoyaDataSourceImageDetails.pending]: (state, action) => {
    state.loading = true;
    state.error = "";
  },
  [requestHoyaDataSourceImageDetails.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
  },
};

/******* Get default datasource image details ********/

export const requestDefaultDataSourceImageDetails = createAsyncThunk(
  `${slices.datasource}/requestDefaultDataSourceImageDetails`,
  async ({ id, params }, { rejectWithValue, dispatch }) => {
    const url = DEFAULT_IMAGE_DETAILS_ENDPOINT.replace(":id", id);
    try {
      const response = await axiosIns.get(url + "?" + serializeQueryParams(params));
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const requestDefaultDataSourceImageDetailsStatus = {
  [requestDefaultDataSourceImageDetails.fulfilled]: (state, action) => {
    state.imageDetails = action.payload;
    state.predictionJudgment = action.payload.judgment;
    state.judgments = {
      data: action?.payload?.judgments?.map?.(({ judgment }) => judgment) || [],
      prevData: action?.payload?.judgments?.map?.(({ judgment }) => judgment) || [],
      dirty: false,
    };
    state.loading = false;
    state.error = "";
  },
  [requestDefaultDataSourceImageDetails.pending]: (state, action) => {
    state.loading = true;
    state.error = "";
    state.judgments = {
      data: [],
      prevData: [],
      dirty: false,
    };
  },
  [requestDefaultDataSourceImageDetails.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
    state.judgments = {
      data: [],
      prevData: [],
      dirty: false,
    };
  },
};

/** Edit data source streaming */

export const requestEditDataSourceStreaming = createAsyncThunk(
  `${slices.datasource}/requestEditDataSourceStreaming`,
  async ({ id, data }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.put(`${ENDPOINT}/${id}`, data);
      if (response.status === 200) {
        dispatch(successMessage(response.data.message));
        return response.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const requestEditDataSourceStreamingStatus = {
  [requestEditDataSourceStreaming.fulfilled]: (state, action) => {
    state.loading = false;
    state.error = "";
  },
  [requestEditDataSourceStreaming.pending]: (state, action) => {
    state.loading = true;
    state.error = "";
  },
  [requestEditDataSourceStreaming.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
  },
};

/** Get monitor plate data */

export const requestMonitorPlateData = createAsyncThunk(
  `${slices.datasource}/requestMonitorPlateData`,
  async ({ id, dataId, type }, { rejectWithValue, dispatch }) => {
    try {
      const endpoint = getBaseEndpoint(type);
      const response = await axiosIns.get(`${endpoint}/${id}/data/${dataId}`);
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response?.data?.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const requestMonitorPlateDataStatus = {
  [requestMonitorPlateData.fulfilled]: (state, action) => {
    state.loading = false;
    state.plateData = action.payload;
  },
  [requestMonitorPlateData.pending]: (state, action) => {
    state.loading = true;
  },
  [requestMonitorPlateData.rejected]: (state, action) => {
    state.loading = false;
    state.plateData = {};
  },
};

/** Rerun monitor plate data */

export const requestRerunMonitorPlateData = createAsyncThunk(
  `${slices.datasource}/requestRerunMonitorPlateData`,
  async ({ id, dataId, type, rowIndex }, { rejectWithValue, dispatch }) => {
    try {
      const endpoint = getBaseEndpoint(type);
      const response = await axiosIns.post(`${endpoint}/${id}/data/${dataId}/rerun`);
      dispatch(successMessage("Plate is rerunning successfully"));
      return { data: response.data.data, rowIndex };
    } catch (error) {
      const errorMsg = error?.response?.data?.error || error.message;
      dispatch(errorMessage(errorMsg));
      return rejectWithValue(errorMsg);
    }
  }
);

export const requestRerunMonitorPlateDataStatus = {
  [requestRerunMonitorPlateData.fulfilled]: (state, action) => {
    if (!action.payload) return;
    const { rowIndex } = action?.payload;
    state.loading = false;
    /** @type{DataSourceMonitorData} */
    const itemData = state.itemData;
    let row = itemData.getRows()[rowIndex];
    // reset elapsed time and status
    row[3] = 0;
    row[4] = false;
    state.itemData = itemData.resetPlateStatus(rowIndex, row);
  },
  [requestRerunMonitorPlateData.pending]: (state, action) => {
    state.loading = true;
  },
  [requestRerunMonitorPlateData.rejected]: (state, action) => {
    state.loading = false;
  },
};

/** Download monitor data results */
export const requestDownloadMonitorDataResults = async (id, params, type) => {
  const endpoint = getBaseEndpoint(type);
  const url = `${endpoint}/${id}/data/download-results?${serializeQueryParams(params)}`;
  try {
    const response = await blobAxios.get(url);
    downloadBlob(response.data, "results.zip");
    return response;
  } catch (error) {
    notification.error({
      message: "Error!",
      description: error?.response?.data?.error || error.message,
    });
  }
};

/******* Get datasource image logs ********/

export const requestDataSourceImageLogs = createAsyncThunk(
  `${slices.datasource}/requestDataSourceImageLogs`,
  async ({ imageId, type }, { rejectWithValue, dispatch }) => {
    try {
      const endpoint = getBaseEndpoint(type);
      const response = await axiosIns.get(`${endpoint}/images/${imageId}/logs`);
      if (response.status === 200) {
        return response.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

/** Add missing plate data */

export const requestAddMissingPlate = createAsyncThunk(
  `${slices.datasource}/requestMonitorPlateData`,
  async ({ id, data }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.post(`${MONITOR_PLATE_DATA_ENDPOINT}/${id}/missed-plate`, data);
      if (response.status === 200) {
        dispatch(successMessage(response.data.data));
        return response.data.data;
      }
      throw new Error(response?.data?.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

/** Get monitor plate data */

export const requestMonitorPredictions = createAsyncThunk(
  `${slices.datasource}/requestMonitorPredictions`,
  async ({ id, filters }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.get(`${ENDPOINT}/${id}/explorer?${serializeQueryParams(filters)}`);
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response?.data?.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const requestMonitorPredictionsStatus = {
  [requestMonitorPredictions.fulfilled]: (state, action) => {
    const { data, current_page, records_count, page_limit } = action.payload;
    state.predictions.data = data?.map(Prediction.fromAPI) || [];
    state.predictions.currentPage = current_page;
    state.predictions.pageLimit = page_limit;
    state.predictions.recordsCount = records_count;
    state.predictions.dataLoading = false;
  },
  [requestMonitorPredictions.pending]: (state, action) => {
    state.predictions.dataLoading = true;
  },
  [requestMonitorPredictions.rejected]: (state, action) => {
    state.predictions.dataLoading = false;
  },
};

/** Rerun monitor prediction */
export const requestMonitorRerunPrediction = createAsyncThunk(
  `${slices.datasource}/requestMonitorRerunPrediction`,
  async ({ id, data, onSuccess }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.post(`${ENDPOINT}/${id}/explorer/rerun`, data);
      if (response.status === 200) {
        if (onSuccess) {
          onSuccess();
        }
      }
      throw new Error(response?.data?.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

/** Get images counts */
export const requestMonitorImageCount = createAsyncThunk(
  `${slices.datasource}/requestMonitorImageCount`,
  async ({ id, filters }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.get(`${ENDPOINT}/${id}/images/count?${serializeQueryParams(filters)}`);
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response?.data?.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const requestMonitorImageCountStatus = {
  [requestMonitorImageCount.fulfilled]: (state, action) => {
    const imageCounts = action.payload;
    state.imageCounts.data = imageCounts;
    state.imageCounts.loading = false;
  },
  [requestMonitorImageCount.pending]: (state, action) => {
    state.imageCounts.loading = true;
  },
  [requestMonitorImageCount.rejected]: (state, action) => {
    state.imageCounts.loading = false;
  },
};

/** Assign user to unreview images */
export const requestAssignMonitorImages = createAsyncThunk(
  `${slices.datasource}/requestUpdateMonitorAssignImages`,
  async ({ id, data }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.post(`${ENDPOINT}/${id}/tasks`, data);
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response?.data?.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

/** Assign user to unreview images */
export const requestUpdateMonitorAssignImages = createAsyncThunk(
  `${slices.datasource}/requestUpdateMonitorAssignImages`,
  async ({ id, data }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.put(`${ENDPOINT}/${id}/tasks`, data);
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response?.data?.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

/** Unassign assignment */
export const requestUnassignMonitorAssignment = createAsyncThunk(
  `${slices.datasource}/requestUpdateMonitorAssignImages`,
  async ({ id, data }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.post(`/monitors/${id}/tasks/unassign`, data);
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response?.data?.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const requestUpdateMonitorAssignImagesStatus = {
  [requestUpdateMonitorAssignImages.fulfilled]: (state) => {
    state.monitorAssignImageUpdated = true;
  },
  [requestUpdateMonitorAssignImages.pending]: (state) => {
    state.monitorAssignImageUpdated = false;
  },
  [requestUpdateMonitorAssignImages.rejected]: (state) => {
    state.monitorAssignImageUpdated = false;
  },
};

/** Get list of unreviews assignments */
export const requestMonitorAssignments = createAsyncThunk(
  `${slices.datasource}/requestMonitorAssignments`,
  async ({ id, filters }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.get(`${ENDPOINT}/${id}/tasks?${serializeQueryParams(filters)}`);
      if (response.status === 200) {
        const assignments = response.data.data.map((item) => Assignment.fromApi(item));
        return assignments;
      }
      throw new Error(response?.data?.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const requestMonitorAssignmentsStatus = {
  [requestMonitorAssignments.fulfilled]: (state, action) => {
    state.assignments.data = action.payload;
    state.assignments.loading = false;
  },
  [requestMonitorAssignments.pending]: (state) => {
    state.assignments.loading = true;
  },
  [requestMonitorAssignments.rejected]: (state) => {
    state.assignments.loading = false;
  },
};

/** Update monitor prediction judgment */

export const requestUpdateMonitorPredictionJudgment = createAsyncThunk(
  `${slices.datasource}/requestUpdateMonitorPredictionJudgment`,
  async ({ id, imageId, data }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.patch(`${ENDPOINT}/${id}/predictions/${imageId}/review`, data);
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response?.data?.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestUpdateMonitorPredictionJudgmentStatus = {
  [requestUpdateMonitorPredictionJudgment.fulfilled]: (state, action) => {
    if (action.payload) {
      state.predictionJudgment = action.payload;
      state.predictionJudgmentUpdated = true;
      state.judgments = {
        data: action?.payload?.map?.(({ judgment }) => judgment) || [],
        prevData: action?.payload?.map?.(({ judgment }) => judgment) || [],
        dirty: false,
      };
    }

    state.loadingJudgment = false;
  },
  [requestUpdateMonitorPredictionJudgment.pending]: (state, action) => {
    state.predictionJudgmentUpdated = false;
    state.loadingJudgment = true;
  },
  [requestUpdateMonitorPredictionJudgment.rejected]: (state, action) => {
    state.predictionJudgmentUpdated = false;
    state.loadingJudgment = false;
    state.error = action.payload;
  },
};

/** Exlude an image */
export const requestUpdateMonitorPredictionExclude = createAsyncThunk(
  `${slices.datasource}/requestUpdateMonitorPredictionExclude`,
  async ({ id, imageId, data, onSuccess }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.patch(`${ENDPOINT}/${id}/predictions/${imageId}/exclude`, data);
      if (response.status === 200) {
        onSuccess();
        return response.data.data;
      }
      throw new Error(response?.data?.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestUpdateMonitorPredictionExcludeStatus = {
  [requestUpdateMonitorPredictionExclude.fulfilled]: (state, action) => {
    state.loadingExclude = false;
  },
  [requestUpdateMonitorPredictionExclude.pending]: (state) => {
    state.loadingExclude = true;
  },
  [requestUpdateMonitorPredictionExclude.rejected]: (state) => {
    state.loadingExclude = false;
  },
};
/** Get monitor image adjacent */

export const requestMonitorImageAdjacent = createAsyncThunk(
  `${slices.datasource}/requestMonitorImageAdjacent`,
  async ({ id, imageId, filters }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.get(
        `${ENDPOINT}/${id}/predictions/${imageId}/adjacent?${serializeQueryParams(filters)}`
      );
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response?.data?.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const requestMonitorImageAdjacentStatus = {
  [requestMonitorImageAdjacent.fulfilled]: (state, action) => {
    const data = action.payload;
    state.imageAdjacent = data ? Adjacent.fromAPI(data) : Adjacent.newInitial();
    state.loading = false;
  },
  [requestMonitorImageAdjacent.pending]: (state, action) => {
    state.loading = true;
  },
  [requestMonitorImageAdjacent.rejected]: (state, action) => {
    state.loading = false;
  },
};

/** Update external objects */
export const requestUpdateMonitorExternalObject = createAsyncThunk(
  `${slices.datasource}/requestMonitorUpdateExternalObject`,
  async ({ id, imageId, data, onSuccess }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.patch(`${ENDPOINT}/${id}/predictions/${imageId}/objects`, data);
      if (response.status === 200) {
        onSuccess();
        return response.data.data;
      }
      throw new Error(response?.data?.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);
/** Get monitor review stats */

export const requestMonitorReviewStats = createAsyncThunk(
  `${slices.datasource}/requestMonitorReviewStats`,
  async ({ id, filters, cancelToken }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.get(
        `${ENDPOINT}/${id}/predictions/review-stats?${serializeQueryParams(filters)}`,
        { cancelToken }
      );
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response?.data?.error);
    } catch (error) {
      if (Axios.isCancel(error)) {
        return rejectWithValue("Request canceled");
      }
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const requestMonitorReviewStatsStatus = {
  [requestMonitorReviewStats.fulfilled]: (state, action) => {
    const data = action.payload;
    state.reviewStats = data ? ReviewStats.fromAPI(data) : ReviewStats.newInitial();
    state.predictions.loading = false;
  },
  [requestMonitorReviewStats.pending]: (state, action) => {
    state.predictions.loading = true;
  },
  [requestMonitorReviewStats.rejected]: (state, action) => {
    state.predictions.loading = false;
  },
};

/** Get monitor plate data */

export const requestMonitorLabels = createAsyncThunk(
  `${slices.datasource}/requestMonitorLabels`,
  async ({ id }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.get(`${ENDPOINT}/${id}/labels`);
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response?.data?.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const requestMonitorLabelsStatus = {
  [requestMonitorLabels.fulfilled]: (state, action) => {
    state.predictions.labels = action.payload || [];
    state.predictions.loading = false;
  },
  [requestMonitorLabels.pending]: (state, action) => {
    state.predictions.loading = true;
  },
  [requestMonitorLabels.rejected]: (state, action) => {
    state.predictions.loading = false;
  },
};

/** Get monitor used models */

export const requestMonitorUsedModels = createAsyncThunk(
  `${slices.datasource}/requestMonitorUsedModels`,
  async ({ id }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.get(`${ENDPOINT}/${id}/predictions/used-models`);
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response?.data?.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const requestMonitorUsedModelsStatus = {
  [requestMonitorUsedModels.fulfilled]: (state, action) => {
    state.predictions.usedModels = action.payload || [];
    state.predictions.loading = false;
  },
  [requestMonitorUsedModels.pending]: (state, action) => {
    state.predictions.loading = true;
  },
  [requestMonitorUsedModels.rejected]: (state, action) => {
    state.predictions.loading = false;
  },
};

/** Get monitor judgment stats */

export const requestMonitorJudgmentStats = createAsyncThunk(
  `${slices.datasource}/requestMonitorJudgmentStats`,
  async ({ id, filters, cancelToken }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.get(
        `${ENDPOINT}/${id}/predictions/judgment-stats?${serializeQueryParams(filters)}`,
        { cancelToken }
      );
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response?.data?.error);
    } catch (error) {
      if (Axios.isCancel(error)) {
        return rejectWithValue("Request canceled");
      }
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const requestMonitorJudgmentStatsStatus = {
  [requestMonitorJudgmentStats.fulfilled]: (state, action) => {
    const { pass_with_external_objects = 0, ...rest } = action.payload;
    state.judgmentStats = { ...rest, pass_with_missed_defects: pass_with_external_objects } || {};
    state.predictions.loading = false;
  },
  [requestMonitorJudgmentStats.pending]: (state, action) => {
    state.predictions.loading = true;
  },
  [requestMonitorJudgmentStats.rejected]: (state, action) => {
    state.predictions.loading = false;
  },
};

/** Get monitor external object judgment stats */

export const requestMonitorExternalObjectJudgmentStats = createAsyncThunk(
  `${slices.datasource}/requestMonitorExternalObjectJudgmentStats`,
  async ({ id, filters, cancelToken }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.get(
        `${ENDPOINT}/${id}/predictions/external-objects-judgments-stats?${serializeQueryParams(filters)}`,
        { cancelToken }
      );
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response?.data?.error);
    } catch (error) {
      if (Axios.isCancel(error)) {
        return rejectWithValue("Request canceled");
      }
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const requestMonitorExternalObjectJudgmentStatsStatus = {
  [requestMonitorExternalObjectJudgmentStats.fulfilled]: (state, action) => {
    const { user_external_objects = 0, ...rest } = action.payload;
    state.externalObjectJudgmentStats = { ...rest, false_negatives: user_external_objects } || {};
    state.predictions.loading = false;
  },
  [requestMonitorExternalObjectJudgmentStats.pending]: (state, action) => {
    state.predictions.loading = true;
  },
  [requestMonitorExternalObjectJudgmentStats.rejected]: (state, action) => {
    state.predictions.loading = false;
  },
};

/** Rerun image prediction */

export const requestRerunImagePrediction = createAsyncThunk(
  `${slices.datasource}/requestRerunImagePrediction`,
  async ({ id, imageId, imagePath }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.post(`${ENDPOINT}/${id}/predictions/${imageId}/rerun?is_priority=true`);
      if (response.status === 200) {
        dispatch(requestDefaultDataSourceImageDetails({ id, params: { path: imagePath } }));
        return response.data.data;
      }
      throw new Error(response?.data?.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const requestRerunImagePredictionStatus = {
  [requestRerunImagePrediction.fulfilled]: (state, action) => {
    state.loading = false;
  },
  [requestRerunImagePrediction.pending]: (state, action) => {
    state.loading = true;
  },
  [requestRerunImagePrediction.rejected]: (state, action) => {
    state.loading = false;
  },
};

/** Rerun multiple images prediction */

export const requestRerunMultipleImagesPrediction = createAsyncThunk(
  `${slices.datasource}/requestRerunMultipleImagesPrediction`,
  async ({ id, filters }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.post(
        `${ENDPOINT}/${id}/predictions/rerun-multiple?is_priority=true&${serializeQueryParams(filters)}`
      );
      if (response.status === 200) {
        dispatch(requestMonitorPredictions({ id, filters }));
        return response.data.data;
      }
      throw new Error(response?.data?.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const requestRerunMultipleImagesPredictionStatus = {
  [requestRerunMultipleImagesPrediction.fulfilled]: (state, action) => {
    state.loading = false;
  },
  [requestRerunMultipleImagesPrediction.pending]: (state, action) => {
    state.loading = true;
  },
  [requestRerunMultipleImagesPrediction.rejected]: (state, action) => {
    state.loading = false;
  },
};
export const requestMonitorPredictionsExport = createAsyncThunk(
  `${slices.datasource}/requestMonitorPredictionsExport`,
  async ({ id, filters }, { rejectWithValue, dispatch }) => {
    return axiosIns
      .get(`${ENDPOINT}/${id}/explorer/export-folder?${serializeQueryParams(filters)}`, {
        responseType: "arraybuffer",
      })
      .then((response) => {
        if (response.status === 200) {
          return response.data;
        }
      })
      .catch((error) => {
        let errorMsg;
        if (error.response) {
          const errorText = new TextDecoder().decode(error.response.data);
          try {
            const errorJson = JSON.parse(errorText);
            errorMsg = errorJson.error || "Unknown error during export";
          } catch {
            errorMsg = errorText || "Unknown error during export";
          }
        } else {
          errorMsg = error.message || "Error fetching export data";
        }

        notification.error({
          message: "Export Error",
          description: errorMsg,
        });

        return rejectWithValue(errorMsg);
      });
  }
);

export const requestMonitorStats = createAsyncThunk(
  `${slices.monitor}/requestMonitorStats`,
  async ({ organization, monitorId, from, to }, { rejectWithValue, dispatch }) => {
    const params = {
      ...(organization && { Organization: organization }),
      ...(monitorId && { MonitorId: monitorId }),
      ...(from && { From: from }),
      ...(to && { To: to }),
    };

    const url = `${ENDPOINT}/stats?${serializeQueryParams(params)}`;
    try {
      const response = await axiosIns.get(url);
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response.data.error || "Failed to fetch monitor stats");
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message || "An error occurred";
      dispatch(errorMessage(errorMsg));
      return rejectWithValue(errorMsg);
    }
  }
);

export const requestMonitorStatsStatus = {
  [requestMonitorStats.fulfilled]: (state, action) => {
    state.monitorStats = action.payload;
    state.monitorStats.loading = false;
    state.monitorStats.error = null;
  },
  [requestMonitorStats.pending]: (state, action) => {
    state.monitorStats = {
      ...state.monitorStats,
      loading: true,
      error: null,
    };
  },
  [requestMonitorStats.rejected]: (state, action) => {
    state.monitorStats = {
      ...state.monitorStats,
      loading: false,
      error: action.payload,
    };
  },
};

export const requestAllMonitorsStats = createAsyncThunk(
  `${slices.monitor}/requestAllMonitorsStats`,
  async ({ from, to, organization, page }, { rejectWithValue, dispatch }) => {
    const params = {
      ...(from && { from: from }),
      ...(to && { to: to }),
      ...(organization && { organization: organization }),
      ...(page && { page: page }),
    };

    const url = `${ENDPOINT}/predictions-stats?${serializeQueryParams(params)}`;
    try {
      const response = await axiosIns.get(url);
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response.data.error || "Failed to fetch all monitors stats");
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message || "An error occurred";
      dispatch(errorMessage(errorMsg));
      return rejectWithValue(errorMsg);
    }
  }
);

export const requestAllMonitorsStatsStatus = {
  [requestAllMonitorsStats.fulfilled]: (state, action) => {
    state.allMonitorsStats.data = action.payload;
    state.allMonitorsStats.loading = false;
    state.allMonitorsStats.error = null;
  },
  [requestAllMonitorsStats.pending]: (state, action) => {
    state.allMonitorsStats = {
      ...state.allMonitorsStats,
      loading: true,
      error: null,
    };
  },
  [requestAllMonitorsStats.rejected]: (state, action) => {
    state.allMonitorsStats = {
      ...state.allMonitorsStats,
      loading: false,
      error: action.payload,
    };
  },
};

export const requestDownloadMonitorsErrors = async (jobId) => {
  const url = `${ENDPOINT}/predictions-stats/${jobId}/export-errors`;
  try {
    const response = await blobAxios.get(url);
    downloadBlob(response.data, `prediction-errors-${jobId}.xlsx`);
    return response;
  } catch (error) {
    notification.error({
      message: "Error!",
      description: error?.response?.data?.error || error.message,
    });
  }
};
